import React from 'react';
import { SEO } from '../components/seo/SEO';
import { client, urlFor, generateSrcSet, urlForOptimized } from '../lib/sanity';
import { StoryDate } from '../components/common/StoryDate';
import { Heart, Quote, MapPin, PawPrint, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SuccessStory } from '../types/sanity';
import { getBreadcrumbSchema } from '../lib/jsonLd';

type AdoptionType = 'all' | 'direct' | 'virtual';

export function AdoptionStoriesPage() {
  const [stories, setStories] = React.useState<SuccessStory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedStory, setSelectedStory] = React.useState<string | null>(null);
  const [adoptionType, setAdoptionType] = React.useState<AdoptionType>('all');
  const navigate = useNavigate();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Historie adopcyjne', url: 'https://znajdki.pl/historie-adopcyjne' }
  ]);

  const filteredStories = React.useMemo(() => {
    if (adoptionType === 'all') return stories;
    return stories.filter(story => story.adoptionType === adoptionType);
  }, [stories, adoptionType]);

  React.useEffect(() => {
    const fetchStories = async () => {
      try {
        const result = await client.fetch<SuccessStory[]>(`
          *[_type == "successStory" && isPublished == true && !(_id in path("drafts.**"))] | order(adoptionDate desc) {
            _id,
            ownerName,
            adoptionDate,
            location,
            quote,
            testimony,
            impactQuote,
            image,
            media,
            wasSpecialCase,
            adoptionType,
            "pet": pet->{
              name,
              mainCategory,
              image
            },
            "additionalPets": additionalPets[]->{
              name,
              mainCategory,
              image
            }
          }
        `);
        setStories(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching success stories:', err);
        setError('Nie udało się załadować historii adopcyjnych');
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center">
          <p className="text-xl text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
            Ładowanie historii...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center">
          <p className="text-xl text-accessible-red-600">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Historie adopcyjne - Fundacja Znajdki"
        description="Poznaj szczęśliwe historie adopcyjne psów i kotów z Fundacji Znajdki. Zobacz jak nasi podopieczni znaleźli nowe domy i nowych opiekunów."
        url="/historie-adopcyjne"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-4">
          Historie adopcyjne
        </h1>
        <p className="body-large max-w-3xl mx-auto mb-8">
          Poznaj historie naszych podopiecznych, którzy znaleźli swoje miejsce na ziemi. 
          Każda z tych opowieści to dowód na to, że warto dawać szansę bezdomnym zwierzętom.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a 
            href="/adoptuj" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-accessible-red-600 text-white rounded-lg hover:bg-accessible-red-700 transition-all duration-300 transform hover:scale-105 font-medium text-lg shadow-lg"
          >
            <Heart className="w-5 h-5" />
            <span>Adoptuj zwierzaka</span>
          </a>
          <a 
            href="/wirtualna-adopcja" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-white dark:bg-accessible-gray-700 text-accessible-gray-900 dark:text-white rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-all duration-300 transform hover:scale-105 font-medium text-lg shadow-lg border border-accessible-gray-200 dark:border-accessible-gray-600"
          >
            <span>Adopcja wirtualna</span>
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-12 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-xl shadow-lg">
        <div className="flex items-center gap-2 label-medium">
          <Filter className="w-5 h-5" />
          <span>Filtruj:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setAdoptionType('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              adoptionType === 'all'
                ? 'bg-accessible-red-600 text-white btn-text-medium shadow-md transform scale-105'
                : 'bg-accessible-gray-100 dark:bg-accessible-gray-700 text-accessible-gray-600 dark:text-white hover:bg-accessible-gray-200 hover:scale-105 transition-transform duration-300 btn-text-medium'
            }`}
          >
            Wszystkie
          </button>
          <button
            onClick={() => setAdoptionType('direct')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              adoptionType === 'direct'
                ? 'bg-accessible-red-600 text-white btn-text-medium shadow-md transform scale-105'
                : 'bg-accessible-gray-100 dark:bg-accessible-gray-700 text-accessible-gray-600 dark:text-white hover:bg-accessible-gray-200 hover:scale-105 transition-transform duration-300 btn-text-medium'
            }`}
          >
            Adopcje bezpośrednie
          </button>
          <button
            onClick={() => setAdoptionType('virtual')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              adoptionType === 'virtual'
               ? 'bg-accessible-red-600 text-white btn-text-medium shadow-md transform scale-105'
               : 'bg-accessible-gray-100 dark:bg-accessible-gray-700 text-accessible-gray-600 dark:text-white hover:bg-accessible-gray-200 hover:scale-105 transition-transform duration-300 btn-text-medium'
            }`}
          >
            Adopcje wirtualne
          </button>
        </div>
      </div>

      {filteredStories.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-xl shadow-lg">
          <p className="body-large">
            Nie znaleziono historii adopcyjnych dla wybranych kryteriów
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {filteredStories.map((story) => (
            <div 
              key={story._id} 
              className={`bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 ${
                selectedStory === story._id ? 'scale-[1.02] ring-2 ring-accessible-red-600' : 'hover:scale-[1.01]'
              } cursor-pointer`}
              onClick={() => navigate(`/historia/${story._id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/historia/${story._id}`);
                }
              }}
              onMouseEnter={() => setSelectedStory(story._id)}
              onMouseLeave={() => setSelectedStory(null)}
              role="button"
              tabIndex={0}
              title={`Zobacz historię ${typeof story.pet === 'object' && story.pet?.name}${story.additionalPets && story.additionalPets.length > 0 ? ` i ${story.additionalPets.length} ${story.additionalPets.length === 1 ? 'innego zwierzaka' : 'innych zwierzaków'}` : ''}`}
            >
              <div className="relative aspect-video">
                <img
                  src={story.image ? urlForOptimized(story.image, 800, 450, 80) : 
                       typeof story.pet === 'object' && story.pet?.image ? urlForOptimized(story.pet.image, 800, 450, 80) :
                       '/images/animals/fundacja-znajdki-zwierzeta-015.webp'}
                  srcSet={story.image ? generateSrcSet(story.image) : 
                         typeof story.pet === 'object' && story.pet?.image ? generateSrcSet(story.pet.image) : 
                         ''}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt={`${(typeof story.pet === 'object' && story.pet?.name) || 'Podopieczny'} w nowym domu z ${story.ownerName}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="450"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent"></div>
                {story.wasSpecialCase && (
                  <div className="absolute top-4 right-4 bg-accessible-red-600 text-white px-3 py-1 rounded-full text-sm">
                    Wyjątkowy przypadek
                  </div>
                )}
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                  <PawPrint className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {story.adoptionType === 'virtual' ? 'Adopcja wirtualna' : 'Adopcja bezpośrednia'}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-accessible-red-600" />
                  <span className="body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
                    {story.adoptionType === 'virtual' ? 'Wirtualny opiekun' : 'Nowy dom'}: {story.ownerName}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-4">
                  Historia {typeof story.pet === 'object' ? story.pet?.name : ''}
                  {story.additionalPets && story.additionalPets.length > 0 && (
                    <span className="body-large text-accessible-gray-700 dark:text-white/90 high-contrast:text-white">
                      {' '}i {story.additionalPets.length} {story.additionalPets.length === 1 ? 'innego zwierzaka' : 'innych zwierzaków'}
                    </span>
                  )}
                </h2>
                <Quote className="w-8 h-8 text-accessible-red-600/30 mb-4" />
                <blockquote className="quote-text mb-6">
                  "{story.quote}"
                </blockquote>
                {story.impactQuote && (
                  <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg mb-6">
                    <p className="body-medium italic">
                      "{story.impactQuote}"
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-4 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
                  {story.adoptionDate && (
                    <StoryDate date={new Date(story.adoptionDate).toLocaleDateString('pl-PL', {
                      year: 'numeric',
                      month: 'long'
                    })} />
                  )}
                  {story.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{story.location}</span>
                    </div>
                  )}
                </div>
                {story.media && story.media.length > 0 && (
                  <div className="mt-6 grid grid-cols-4 gap-2">
                    {story.media.slice(0, 4).map((media: any, index: number) => (
                      'asset' in media ? (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                          <img
                            src={(urlFor(media as any) as any).width(200).height(200).url()}
                            alt={`Dodatkowe zdjęcie ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                          {index === 3 && story.media && story.media.length > 4 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white">
                              <span>+{story.media.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Support CTA */}
      <div className="bg-accessible-red-600 rounded-2xl p-12 mt-16 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-3-white mb-4">
              Pomóż nam tworzyć więcej szczęśliwych historii
            </h2>
            <p className="body-medium-white-90 mb-8">
              Każda złotówka przekazana na rzecz fundacji to szansa na nowy dom dla kolejnego zwierzaka.
              Wspierając nas, pomagasz bezdomnym zwierzętom znaleźć kochającą rodzinę.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a 
                href="/wsparcie" 
                className="bg-white text-accessible-red-600 px-6 py-4 rounded-lg btn-text-medium hover:bg-accessible-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
                title="Wesprzyj nasze działania"
              >
                <Heart className="w-5 h-5" />
                <span>Wesprzyj teraz</span>
              </a>
              <a 
                href="/przekaz-podatek" 
                className="bg-white text-accessible-red-600 px-6 py-4 rounded-lg btn-text-medium hover:bg-accessible-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
                title="Przekaż 1.5% podatku"
              >
                <span>Przekaż 1.5% podatku</span>
              </a>
              <a 
                href="/dom-tymczasowy" 
                className="bg-white text-accessible-red-600 px-6 py-4 rounded-lg btn-text-medium hover:bg-accessible-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
                title="Zostań domem tymczasowym"
              >
                <span>Dom tymczasowy</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}